import type { FullscreenPort } from '@Domain/Ports/Fullscreen/FullscreenPort';

/** The page element that goes fullscreen, with the WebKit-prefixed request kept for older Safari. */
type FullscreenRoot = {
	requestFullscreen?: () => Promise<void>;
	webkitRequestFullscreen?: () => Promise<void> | void;
};

/** The slice of `Document` this adapter needs, standard and WebKit-prefixed variants included. */
type FullscreenDocument = {
	readonly documentElement: FullscreenRoot;
	readonly fullscreenEnabled?: boolean;
	readonly webkitFullscreenEnabled?: boolean;
	readonly fullscreenElement?: Element | null;
	readonly webkitFullscreenElement?: Element | null;
	exitFullscreen?: () => Promise<void>;
	webkitExitFullscreen?: () => Promise<void> | void;
	addEventListener(type: 'fullscreenchange' | 'webkitfullscreenchange', listener: () => void): void;
	removeEventListener(type: 'fullscreenchange' | 'webkitfullscreenchange', listener: () => void): void;
};

/**
 * Puts the whole page (`document.documentElement`) in the browser's fullscreen.
 *
 * Nothing is read or listened to at construction. `enter()` calls the platform request
 * synchronously (no `await` before it) so that it still runs inside the user gesture; a refusal is
 * swallowed, the caller falls back on its own layout. iPhone Safari exposes no page fullscreen:
 * `isSupported()` is then false and `enter()` / `exit()` do nothing.
 */
class WebFullscreenAdapter implements FullscreenPort {
	constructor(private readonly target: FullscreenDocument = document) {}

	isSupported(): boolean {
		const root = this.target.documentElement;
		const hasRequest =
			typeof root.requestFullscreen === 'function' ||
			typeof root.webkitRequestFullscreen === 'function';
		const isEnabled =
			this.target.fullscreenEnabled === true || this.target.webkitFullscreenEnabled === true;

		return hasRequest && isEnabled;
	}

	isActive(): boolean {
		const element = this.target.fullscreenElement ?? this.target.webkitFullscreenElement ?? null;

		return element !== null && element === this.target.documentElement;
	}

	async enter(): Promise<void> {
		if (!this.isSupported() || this.isActive()) {
			return;
		}
		const root = this.target.documentElement;
		try {
			if (typeof root.requestFullscreen === 'function') {
				await root.requestFullscreen();
			} else {
				await root.webkitRequestFullscreen?.();
			}
		} catch {
			// Refused (permission, call outside a user gesture): the caller keeps its CSS layout.
		}
	}

	async exit(): Promise<void> {
		if (!this.isActive()) {
			return;
		}
		try {
			if (typeof this.target.exitFullscreen === 'function') {
				await this.target.exitFullscreen();
			} else {
				await this.target.webkitExitFullscreen?.();
			}
		} catch {
			// Already left, or refused: nothing to recover.
		}
	}

	subscribe(listener: (isActive: boolean) => void): () => void {
		const handleChange = (): void => {
			listener(this.isActive());
		};
		this.target.addEventListener('fullscreenchange', handleChange);
		this.target.addEventListener('webkitfullscreenchange', handleChange);

		return () => {
			this.target.removeEventListener('fullscreenchange', handleChange);
			this.target.removeEventListener('webkitfullscreenchange', handleChange);
		};
	}
}

export default WebFullscreenAdapter;
export type { FullscreenDocument, FullscreenRoot };
